import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import { deleteDoc, collection, query, where, getDocs, getDoc, doc, orderBy } from "firebase/firestore";
import { getUserAccessInfo } from "@/integrations/firebase/auth";
import { dualReadJob, dualWriteJob, dualSoftDeleteJob, dualReadJobDocuments } from "@/lib/firestore-helpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { sanitizeErrorMessage } from "@/lib/utils";
import { ArrowLeft, ArrowRight, Edit, Trash2, ShieldCheck, Loader2, CheckCircle, MapPin, Search, Package, FileCheck, ClipboardList, Download, ThumbsUp, ThumbsDown, Play, CheckSquare, Bookmark } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { JobInfo } from "@/components/job-detail/JobInfo";
import { JobDocuments, type JobDocumentsHandle } from "@/components/job-detail/JobDocuments";
import { JobPhotos } from "@/components/job-detail/JobPhotos";
import { EditJob } from "@/components/job-detail/EditJob";
import { InspectionStatusCard } from "@/components/job-detail/InspectionStatusCard";
import { SaveAsTemplateDialog } from "@/components/job-detail/SaveAsTemplateDialog";
import { useLookupJurisdiction, useFloridaCounties, type ComplianceCheckResult } from "@/hooks/useCompliance";
import { useJurisdictionNotes, useChecklistRuns, useChecklistTemplates, useCreateChecklistRun, useVoteOnNote } from "@/hooks/useInspections";
import { ContextualTips } from "@/components/inspection/ContextualTips";
import { JobChecklist } from "@/components/job-detail/checklist";
import type { JobChecklist as JobChecklistType } from "@/types/inspection";
import { archiveJobDocumentsToVault } from "@/services/evidenceVaultService";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ComplianceScore, JurisdictionCard, ComplianceChecklist, FindingsList } from "@/components/compliance";
import { QuickReferenceCard } from "@/components/compliance/QuickReferenceCard";
import { TradeRequirementsCard } from "@/components/compliance/TradeRequirementsCard";
import { CommercialRequirementsCard } from "@/components/compliance/CommercialRequirementsCard";
import { SubmissionGuidance } from "@/components/compliance/SubmissionGuidance";
import { ReviewComments } from "@/components/compliance/ReviewComments";
import { AddReviewCommentDialog } from "@/components/compliance/AddReviewCommentDialog";
import { ImportEmailCommentsDialog } from "@/components/compliance/ImportEmailCommentsDialog";
import { ThreePartProgress, calculateDataCompleteness, calculatePacketCompleteness, calculateComplianceReadiness } from "@/components/compliance/ThreePartProgress";
import { RiskSummaryBanner, buildRiskItemsFromFindings } from "@/components/compliance/RiskSummaryBanner";
import { AuditTrail } from "@/components/compliance/AuditTrail";
import { ConnectedAuditTrail } from "@/components/compliance/ConnectedAuditTrail";
import { MarkAsSubmittedDialog, type SubmissionData } from "@/components/compliance/MarkAsSubmittedDialog";
import { PreSubmissionChecklist } from "@/components/compliance/PreSubmissionChecklist";
import { UserFormLibrary, FormRecommendations } from "@/components/forms";
import { FormGenerationDialog } from "@/components/compliance/FormGenerationDialog";
import { useAuditTrail } from "@/hooks/useAuditTrail";
import type { AuditEntry } from "@/types/jurisdiction";
import { ConfidenceBadge } from "@/components/ui/confidence-badge";
import { useJurisdictionDetail } from "@/hooks/useJurisdictions";
import { getNocThreshold } from "@/lib/notice-of-commencement";
import { useReviewComments } from "@/hooks/useReviewComments";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export type InspectionStatus = 'pending_verification' | 'verified' | 'scheduled' | 'passed' | 'failed';

type Job = {
  id: string;
  title: string;
  description: string;
  location: string;
  status: "not_started" | "in_progress" | "completed" | "pending_review";
  client_name: string;
  client_email: string;
  client_phone: string;
  complex_name: string;
  unit_number: string;
  assigned_user_id: string;
  created_by: string;
  created_at: string;
  property_id: string | null;
  // Compliance fields
  jurisdictionId: number | null;
  jurisdictionCode: string | null;
  jurisdictionAhjName: string | null;
  jurisdictionOverlays: Record<string, boolean> | null;
  jurisdictionPortalUrl: string | null;
  jurisdictionPhone: string | null;
  jurisdictionEmail: string | null;
  jurisdictionWebsite: string | null;
  permitType: string | null;
  jobScope: string | null;
  // Inspection tracking fields
  inspectionStatus: InspectionStatus | null;
  submittedForPermitsAt: string | null;
  inspectionScheduledAt: string | null;
  inspectionCompletedAt: string | null;
  // Compliance result (persisted from last check)
  complianceLastResult?: ComplianceCheckResult | null;
  // LLM-generated checklist
  checklist?: JobChecklistType | null;
  // Job trade (for checklist generation)
  trade?: string;
  // Additional job context for jurisdiction features
  estimatedValue?: number;
  isOwnerBuilder?: boolean;
};

const JobDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // When accessed from workflow, hide redundant tabs (Documents, Photos, Inspections)
  const isFromWorkflow = searchParams.get('fromWorkflow') === 'true';
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [contractorId, setContractorId] = useState<string>("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string>("");
  const [deleting, setDeleting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [complianceResult, setComplianceResult] = useState<ComplianceCheckResult | null>(null);
  const [showJurisdictionDialog, setShowJurisdictionDialog] = useState(false);
  const [jurisdictionLookupCounty, setJurisdictionLookupCounty] = useState("");
  const [jurisdictionLookupCity, setJurisdictionLookupCity] = useState("");
  const [savingJurisdiction, setSavingJurisdiction] = useState(false);
  const [jobDocuments, setJobDocuments] = useState<Array<{
    id: string;
    category: string;
    fileName: string;
    status?: 'valid' | 'invalid' | 'needs_review' | 'uploaded';
  }>>([]);
  const [activeTab, setActiveTab] = useState("compliance");
  const navigate = useNavigate();
  const { toast } = useToast();

  // Refs
  const documentsRef = useRef<JobDocumentsHandle>(null);

  // Compliance hooks
  const jurisdictionLookup = useLookupJurisdiction();
  const { data: floridaCounties } = useFloridaCounties();

  // Inspection intelligence hooks
  const { data: jurisdictionNotes = [], isLoading: notesLoading } = useJurisdictionNotes(
    job?.jurisdictionId?.toString() || null
  );
  const { data: checklistRuns = [], isLoading: runsLoading } = useChecklistRuns({
    projectId: id,
  });
  const { data: checklistTemplates = [] } = useChecklistTemplates({
    jurisdictionId: job?.jurisdictionId?.toString() || undefined,
  });
  const createChecklistRun = useCreateChecklistRun();
  const voteOnNote = useVoteOnNote();

  // Fetch full jurisdiction data with JSONB fields for Quick Reference and Trade Requirements cards
  const { data: jurisdictionDetail } = useJurisdictionDetail(job?.jurisdictionId || null);
  const nocThreshold = useMemo(() => {
    return getNocThreshold(
      jurisdictionDetail?.notice_of_commencement,
      [job?.permitType || '', job?.trade || ''].filter(Boolean)
    );
  }, [jurisdictionDetail?.notice_of_commencement, job?.permitType, job?.trade]);
  const nocThresholdAmount = nocThreshold?.amount ?? 2500;

  // Review comments state and hook
  const [showAddCommentDialog, setShowAddCommentDialog] = useState(false);
  const [showImportEmailDialog, setShowImportEmailDialog] = useState(false);
  const [editingComment, setEditingComment] = useState<any>(null);

  // Mark as Submitted dialog state
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  // Form Generation dialog state
  const [showFormGenerationDialog, setShowFormGenerationDialog] = useState(false);
  const {
    comments: reviewComments,
    isLoading: reviewCommentsLoading,
    addComment,
    updateComment,
    deleteComment,
    updateStatus: updateCommentStatus,
  } = useReviewComments({ jobId: id || '' });

  // Audit trail hook - for recording submission events
  const {
    addSubmissionEvent,
    addStatusChange,
  } = useAuditTrail({ jobId: id || '' });

  // Use the compliance result from state (populated from job.complianceLastResult or fresh check)
  // The complianceResult state is defined above at line 125 and populated in useEffect

  // Calculate three-part progress
  const dataProgress = useMemo(() => {
    if (!job) return { percentage: 0, missingItems: [] };
    return calculateDataCompleteness(job as Record<string, unknown>);
  }, [job]);

  const packetProgress = useMemo(() => {
    if (!jobDocuments || jobDocuments.length === 0) {
      return { percentage: 0, missingItems: ['No documents uploaded'] };
    }
    // Use document names for comparison with required items
    // For now, use a simple calculation based on expected documents
    const requiredDocs = [
      { name: 'Permit Application', isRequired: true },
      { name: 'Site Plan', isRequired: true },
      { name: 'Load Calculation', isRequired: job?.permitType === 'electrical' },
      { name: 'One-line Diagram', isRequired: job?.permitType === 'electrical' },
    ].filter(d => d.isRequired);
    return calculatePacketCompleteness(requiredDocs, jobDocuments.map(d => d.fileName));
  }, [jobDocuments, job?.permitType]);

  const complianceProgress = useMemo(() => {
    return calculateComplianceReadiness(complianceResult);
  }, [complianceResult]);

  // Build risk items from compliance findings
  const riskData = useMemo(() => {
    if (!complianceResult?.findings) {
      return { blockers: [], reviewItems: [], verifiedCount: 0 };
    }
    const { blockers, reviewItems } = buildRiskItemsFromFindings(complianceResult.findings);
    const verifiedCount = complianceResult.summary?.total
      ? complianceResult.summary.total - (complianceResult.summary.critical || 0) - (complianceResult.summary.warning || 0)
      : 0;
    return { blockers, reviewItems, verifiedCount };
  }, [complianceResult]);

  // Generate audit trail entries from job history (mock for now - later fetch from Firestore)
  const auditEntries = useMemo((): AuditEntry[] => {
    if (!job) return [];
    const entries: AuditEntry[] = [];

    // Add job creation entry
    if (job.created_at) {
      entries.push({
        id: 'create-job',
        jobId: job.id,
        timestamp: job.created_at,
        type: 'user_action',
        actorType: 'user',
        actorId: job.created_by,
        actorName: 'User',
        action: {
          description: 'Created permit job',
          newValue: job.title,
        },
      });
    }

    // Add jurisdiction detection entry if jurisdiction exists
    if (job.jurisdictionId && job.jurisdictionAhjName) {
      entries.push({
        id: 'jurisdiction-detect',
        jobId: job.id,
        timestamp: job.created_at,
        type: 'system_decision',
        actorType: 'system',
        decision: {
          action: 'Auto-filled jurisdiction',
          result: job.jurisdictionAhjName,
          confidence: 'high',
          rules: ['Address geocoding', 'County boundary matching'],
          source: 'Address lookup',
        },
      });
    }

    // Add compliance check entry if result exists
    if (complianceResult) {
      entries.push({
        id: `compliance-check-${complianceResult.checkRunId || 'latest'}`,
        jobId: job.id,
        timestamp: new Date().toISOString(), // Would come from actual check time
        type: 'compliance_check',
        actorType: 'system',
        check: {
          status: complianceResult.status,
          score: complianceResult.score,
          rulesChecked: complianceResult.summary?.total || 0,
          rulesPassed: (complianceResult.summary?.total || 0) - (complianceResult.summary?.critical || 0) - (complianceResult.summary?.warning || 0),
          criticalFindings: complianceResult.summary?.critical || 0,
          warningFindings: complianceResult.summary?.warning || 0,
          durationMs: complianceResult.runDurationMs || 0,
        },
      });
    }

    // Add document upload entries
    jobDocuments.forEach((doc, index) => {
      entries.push({
        id: `doc-${doc.id || index}`,
        jobId: job.id,
        timestamp: new Date().toISOString(), // Would come from actual upload time
        type: 'document_event',
        actorType: 'user',
        actorName: 'User',
        document: {
          action: 'uploaded',
          fileName: doc.fileName,
          fileType: doc.category,
          autoMatched: false,
        },
      });
    });

    return entries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [job, complianceResult, jobDocuments]);

  // Handle starting a new checklist run
  const handleStartChecklist = async (templateId: string) => {
    if (!id || !contractorId) return;
    try {
      await createChecklistRun.mutateAsync({
        projectId: id,
        templateId,
        orgId: contractorId,
      });
      toast({
        title: "Checklist Started",
        description: "A new checklist run has been created for this job.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to start checklist",
        variant: "destructive",
      });
    }
  };

  // Handle voting on a note
  const handleVoteOnNote = async (noteId: string, currentVote: number | null, direction: 1 | -1) => {
    const newVote = currentVote === direction ? 0 : direction;
    try {
      await voteOnNote.mutateAsync({ noteId, voteValue: newVote as 1 | -1 | 0 });
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  // Get note type label
  const getNoteTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      tip: 'Tip',
      pattern: 'Pattern',
      fail_reason: 'Fail Reason',
      preference: 'Preference',
      schedule: 'Scheduling',
    };
    return labels[type] || type;
  };

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate("/auth");
        return;
      }

      // Get comprehensive user access info (handles Personal accounts automatically)
      const accessInfo = await getUserAccessInfo(currentUser.uid);

      // Get contractorId from user document (orgId for org users, contractorId for team members)
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      const userData = userDoc.data();
      const cId = userData?.contractorId ||
                 (userData?.isPrimaryAdmin ? currentUser.uid : currentUser.uid);

      setContractorId(cId); // Store contractorId for dual-write operations
      setIsAdmin(accessInfo.isAdmin);
      setIsManager(accessInfo.isManager);

      fetchJob(cId);
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        checkAuthAndFetch();
      } else {
        navigate("/auth");
      }
    });

    return () => unsubscribe();
  }, [navigate, id]);

  const fetchJob = async (userId: string) => {
    try {
      // Use dual-read to get job from correct structure
      const jobDoc = await dualReadJob(userId, id!);

      if (!jobDoc.exists()) {
        throw new Error("Job not found");
      }

      const jobData = jobDoc.data();
      setJob({
        id: jobDoc.id,
        title: jobData.title,
        description: jobData.description || "",
        location: jobData.location || "",
        status: jobData.status,
        client_name: jobData.client_name || jobData.clientName || "",
        client_email: jobData.client_email || jobData.clientEmail || "",
        client_phone: jobData.client_phone || jobData.clientPhone || "",
        complex_name: jobData.complex_name || jobData.complexName || "",
        unit_number: jobData.unit_number || jobData.unitNumber || "",
        assigned_user_id: jobData.assigned_userId || jobData.assignedUserId || "",
        created_by: jobData.created_by || jobData.createdBy || jobData.contractorId || "",
        created_at: jobData.createdAt?.toDate ? jobData.createdAt.toDate().toISOString() : new Date().toISOString(),
        property_id: jobData.property_id || jobData.propertyId || null,
        // Compliance fields
        jurisdictionId: jobData.jurisdictionId || null,
        jurisdictionCode: jobData.jurisdictionCode || null,
        jurisdictionAhjName: jobData.jurisdictionAhjName || null,
        jurisdictionOverlays: jobData.jurisdictionOverlays || null,
        jurisdictionPortalUrl: jobData.jurisdictionPortalUrl || null,
        jurisdictionPhone: jobData.jurisdictionPhone || null,
        jurisdictionEmail: jobData.jurisdictionEmail || null,
        jurisdictionWebsite: jobData.jurisdictionWebsite || null,
        permitType: jobData.permitType || null,
        jobScope: jobData.jobScope || null,
        // Inspection tracking fields
        inspectionStatus: jobData.inspectionStatus || null,
        submittedForPermitsAt: jobData.submittedForPermitsAt || null,
        inspectionScheduledAt: jobData.inspectionScheduledAt || null,
        inspectionCompletedAt: jobData.inspectionCompletedAt || null,
        // Compliance result (persisted from last check)
        complianceLastResult: jobData.compliance?.lastResult || null,
        // LLM-generated checklist (loaded from job document)
        checklist: jobData.checklist || null,
        // Trade for checklist generation
        trade: jobData.trade || null,
        // Additional job context
        estimatedValue: jobData.estimatedValue || null,
        isOwnerBuilder: jobData.isOwnerBuilder || false,
      });

      // Load persisted compliance result if available
      if (jobData.compliance?.lastResult) {
        setComplianceResult(jobData.compliance.lastResult);
      }
    } catch (error: any) {
      toast({
        title: "Error loading job",
        description: sanitizeErrorMessage(error, "Failed to load job details."),
        variant: "destructive",
      });
      navigate("/jobs");
    } finally {
      setLoading(false);
    }
  };

  // Fetch documents for compliance checklist - runs on initial load
  const fetchJobDocuments = async (cId: string, jobId: string) => {
    try {
      const snapshot = await dualReadJobDocuments(cId, jobId, [
        orderBy("createdAt", "desc")
      ]);

      const docs = snapshot.docs.map(doc => {
        const docData = doc.data();
        return {
          id: doc.id,
          category: docData.category || docData.complianceCategory || '',
          fileName: docData.document_name || '',
          status: docData.status as 'valid' | 'invalid' | 'needs_review' | 'uploaded' | undefined
        };
      });

      setJobDocuments(docs);
    } catch (error) {
      console.warn('Failed to fetch job documents for compliance:', error);
    }
  };

  // Fetch documents when contractorId and job are available
  useEffect(() => {
    if (contractorId && id && !loading) {
      fetchJobDocuments(contractorId, id);
    }
  }, [contractorId, id, loading]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      // Cascade delete all related records
      const jobId = id!;

      // Debug logging for delete permissions
      const currentUser = auth.currentUser;
      console.log("🗑️ DELETE DEBUG:", {
        jobId,
        contractorId,
        currentUserId: currentUser?.uid,
        jobCreatedBy: job?.created_by,
        isAdmin,
        isManager,
        jobData: job
      });

      // Helper to safely delete related records (continues on permission errors)
      const safeDeleteRelated = async (collectionName: string, fieldName: string, fieldValue: string) => {
        try {
          const q = query(collection(db, collectionName), where(fieldName, "==", fieldValue));
          const snapshot = await getDocs(q);
          if (snapshot.docs.length > 0) {
            await Promise.all(snapshot.docs.map((doc) => deleteDoc(doc.ref)));
            console.log(`✅ Deleted ${snapshot.docs.length} records from ${collectionName}`);
          }
        } catch (err) {
          // Log but continue - these are optional cleanup operations
          console.warn(`⚠️ Could not delete from ${collectionName}:`, err);
        }
      };

      const safeDeleteJobDocuments = async () => {
        try {
          const newDocsRef = collection(db, `contractors/${contractorId}/jobs/${jobId}/documents`);
          const newDocsSnapshot = await getDocs(newDocsRef);
          if (newDocsSnapshot.docs.length > 0) {
            await Promise.all(newDocsSnapshot.docs.map((doc) => deleteDoc(doc.ref)));
            console.log(`✅ Deleted ${newDocsSnapshot.docs.length} records from job documents subcollection`);
          }
        } catch (err) {
          console.warn("⚠️ Could not delete from job documents subcollection:", err);
        }

        // Old structure: job_documents
        await safeDeleteRelated("job_documents", "job_id", jobId);
        await safeDeleteRelated("job_documents", "jobId", jobId);
      };

      const safeDeleteLinkedDocuments = async () => {
        try {
          const newDocsRef = collection(db, `contractors/${contractorId}/documents`);
          const [jobIdSnapshot, job_idSnapshot] = await Promise.all([
            getDocs(query(newDocsRef, where("jobId", "==", jobId))),
            getDocs(query(newDocsRef, where("job_id", "==", jobId))),
          ]);
          const docsToDelete = new Map<string, typeof jobIdSnapshot.docs[number]>();
          jobIdSnapshot.docs.forEach((docSnap) => docsToDelete.set(docSnap.ref.path, docSnap));
          job_idSnapshot.docs.forEach((docSnap) => docsToDelete.set(docSnap.ref.path, docSnap));
          if (docsToDelete.size > 0) {
            await Promise.all(Array.from(docsToDelete.values()).map((docSnap) => deleteDoc(docSnap.ref)));
            console.log(`✅ Deleted ${docsToDelete.size} records from contractors documents`);
          }
        } catch (err) {
          console.warn("⚠️ Could not delete from contractors documents:", err);
        }

        // Old structure: documents
        await safeDeleteRelated("documents", "job_id", jobId);
        await safeDeleteRelated("documents", "jobId", jobId);
      };

      // Delete related records (non-blocking - continues on errors)
      await safeDeleteRelated("clientJobs", "jobId", jobId);
      await safeDeleteRelated("messages", "job_id", jobId);
      await safeDeleteRelated("estimates", "job_id", jobId);
      await safeDeleteRelated("job_photos", "job_id", jobId);
      await safeDeleteRelated("material_orders", "job_id", jobId);
      await safeDeleteRelated("daily_reports", "job_id", jobId);
      await safeDeleteRelated("extra_works", "job_id", jobId);
      await safeDeleteRelated("payment_milestones", "job_id", jobId);
      await safeDeleteRelated("time_entries", "job_id", jobId);
      await safeDeleteJobDocuments();
      await safeDeleteLinkedDocuments();

      // Soft delete the job (preserves data for training/analytics)
      const currentUserId = auth.currentUser?.uid || 'unknown';
      console.log("🗑️ About to call dualSoftDeleteJob with:", { contractorId, jobId, deletedBy: currentUserId });
      await dualSoftDeleteJob(contractorId, jobId, currentUserId);
      console.log("✅ dualSoftDeleteJob completed successfully");

      toast({
        title: "Success",
        description: "Permit has been deleted successfully",
      });
      navigate("/jobs");
    } catch (error: any) {
      console.error("❌ DELETE ERROR:", {
        errorMessage: error.message,
        errorCode: error.code,
        errorDetails: error,
        contractorId,
        jobId: id,
        currentUserId: auth.currentUser?.uid
      });
      toast({
        title: "Error deleting job",
        description: sanitizeErrorMessage(error, "Failed to delete job."),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleStatusChange = (newStatus: string) => {
    setPendingStatus(newStatus);
    setShowStatusDialog(true);
  };

  const confirmStatusChange = async () => {
    setUpdatingStatus(true);
    try {
      // Use dual-write to update job status in both structures
      await dualWriteJob(contractorId, id!, {
        status: pendingStatus,
      });

      // Update local state
      if (job) {
        setJob({
          ...job,
          status: pendingStatus as any,
        });
      }

      toast({
        title: "Status Updated",
        description: `Job status changed to ${getStatusLabel(pendingStatus)}. This change is now visible in the client portal.`,
      });
    } catch (error: any) {
      toast({
        title: "Error updating status",
        description: sanitizeErrorMessage(error, "Failed to update job status."),
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(false);
      setShowStatusDialog(false);
      setPendingStatus("");
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      not_started: "Not Started",
      in_progress: "In Progress",
      completed: "Completed",
      pending_review: "Pending Review",
    };
    return labels[status] || status;
  };

  const handleAddJurisdiction = async () => {
    if (!jurisdictionLookupCounty) {
      toast({
        title: "County Required",
        description: "Please select a county to look up jurisdiction.",
        variant: "destructive",
      });
      return;
    }

    setSavingJurisdiction(true);

    try {
      // Look up jurisdiction from the compliance engine
      const result = await jurisdictionLookup.mutateAsync({
        state: "FL",
        county: jurisdictionLookupCounty,
        city: jurisdictionLookupCity || undefined,
      });

      if (!result.success || !result.jurisdiction) {
        throw new Error(result.message || "Jurisdiction not found");
      }

      const jurisdiction = result.jurisdiction;

      // Update the job with jurisdiction data using dual-write
      await dualWriteJob(contractorId, job!.id, {
        jurisdictionId: jurisdiction.id,
        jurisdictionCode: jurisdiction.code,
        jurisdictionAhjName: jurisdiction.ahjName,
        jurisdictionOverlays: jurisdiction.overlays,
        jurisdictionPortalUrl: jurisdiction.portalUrl || null,
        jurisdictionPhone: jurisdiction.phone || null,
        jurisdictionEmail: jurisdiction.email || null,
        jurisdictionWebsite: jurisdiction.website || null,
      });

      // Update local state
      setJob(prev => prev ? {
        ...prev,
        jurisdictionId: jurisdiction.id,
        jurisdictionCode: jurisdiction.code,
        jurisdictionAhjName: jurisdiction.ahjName,
        jurisdictionOverlays: {
          floodZone: jurisdiction.overlays.floodZone,
          coastalZone: jurisdiction.overlays.coastalZone,
          historicDistrict: jurisdiction.overlays.historicDistrict,
          windBorneDebrisRegion: jurisdiction.overlays.windBorneDebrisRegion,
          highVelocityHurricaneZone: jurisdiction.overlays.highVelocityHurricaneZone,
        },
        jurisdictionPortalUrl: jurisdiction.portalUrl || null,
        jurisdictionPhone: jurisdiction.phone || null,
        jurisdictionEmail: jurisdiction.email || null,
        jurisdictionWebsite: jurisdiction.website || null,
      } : null);

      toast({
        title: "Jurisdiction Added",
        description: `${jurisdiction.ahjName} has been set as the permitting authority.`,
      });

      // Close dialog and reset form
      setShowJurisdictionDialog(false);
      setJurisdictionLookupCounty("");
      setJurisdictionLookupCity("");
    } catch (error: any) {
      toast({
        title: "Jurisdiction Lookup Failed",
        description: error.message || "Failed to find jurisdiction. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingJurisdiction(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!job) {
    return null;
  }

  return (
    <div className="bg-background pb-20">
      <header className="bg-card border-b border-border sticky top-0 z-30">
        <div className="flex items-center gap-2 h-14 px-4">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => navigate("/jobs")}
            className="shrink-0 h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold truncate flex-1 min-w-0">{job.title}</h1>
          <div className="flex items-center gap-1 shrink-0">
            {(isAdmin || isManager) && (
              <Select value={job.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-auto gap-1 bg-muted border text-xs px-2 h-8">
                  <span 
                    className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                      job.status === 'not_started' ? 'bg-gray-400' :
                      job.status === 'in_progress' ? 'bg-blue-400' :
                      job.status === 'pending_review' ? 'bg-yellow-400' :
                      'bg-green-400'
                    }`}
                  />
                  <span className="hidden sm:inline">
                    {job.status === 'not_started' ? 'Not Started' :
                     job.status === 'in_progress' ? 'In Progress' :
                     job.status === 'pending_review' ? 'Review' :
                     'Done'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                      Not Started
                    </div>
                  </SelectItem>
                  <SelectItem value="in_progress">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                      In Progress
                    </div>
                  </SelectItem>
                  <SelectItem value="pending_review">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                      Pending Review
                    </div>
                  </SelectItem>
                  <SelectItem value="completed">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                      Completed
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
            {/* Inspection Status Badge */}
            {job.inspectionStatus && (
              <Badge
                variant="outline"
                className={`text-xs cursor-pointer ${
                  job.inspectionStatus === 'pending_verification' ? 'border-amber-400 text-amber-600 bg-amber-50' :
                  job.inspectionStatus === 'verified' ? 'border-blue-400 text-blue-600 bg-blue-50' :
                  job.inspectionStatus === 'scheduled' ? 'border-purple-400 text-purple-600 bg-purple-50' :
                  job.inspectionStatus === 'passed' ? 'border-green-400 text-green-600 bg-green-50' :
                  'border-red-400 text-red-600 bg-red-50'
                }`}
                onClick={() => setActiveTab("inspections")}
                title="View Inspections tab"
              >
                <ClipboardList className="h-3 w-3 mr-1" />
                {job.inspectionStatus === 'pending_verification' ? 'Pending' :
                 job.inspectionStatus === 'verified' ? 'Ready' :
                 job.inspectionStatus === 'scheduled' ? 'Scheduled' :
                 job.inspectionStatus === 'passed' ? 'Passed' : 'Failed'}
              </Badge>
            )}
            {/* Continue Workflow Button - navigates to wizard (primary route) */}
            <Button
              size="sm"
              variant="default"
              onClick={() => navigate(`/jobs/${job.id}`)}
              className="shrink-0"
              title="Continue workflow"
            >
              <ArrowRight className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Workflow</span>
            </Button>
            {/* Save as Template */}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setShowTemplateDialog(true)}
              className="shrink-0 h-8 w-8"
              title="Save as template"
            >
              <Bookmark className="h-4 w-4" />
            </Button>
            {/* Edit/Delete: admins, managers, or job creator */}
            {(isAdmin || isManager || auth.currentUser?.uid === job.created_by) && (
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowEditDialog(true)}
                  className="shrink-0 h-8 w-8"
                  title="Edit job"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="shrink-0 h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => setShowDeleteDialog(true)}
                  title="Delete job"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="p-4 ios-safe-content">
        {/* Compliance-First Tab Structure */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="w-full overflow-x-auto mb-4">
            <TabsList className="inline-flex w-auto min-w-full">
              <TabsTrigger value="compliance" className="gap-1">
                <ShieldCheck className="h-3.5 w-3.5" />
                Compliance
              </TabsTrigger>
              {/* Hide Documents, Photos, Inspections tabs when accessed from workflow */}
              {!isFromWorkflow && <TabsTrigger value="documents">Documents</TabsTrigger>}
              <TabsTrigger value="info">Permit Info</TabsTrigger>
              {!isFromWorkflow && <TabsTrigger value="photos">Photos</TabsTrigger>}
              {!isFromWorkflow && (
                <TabsTrigger value="inspections" className="gap-1">
                  <ClipboardList className="h-3.5 w-3.5" />
                  Inspections
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent value="info" className="space-y-4 mt-4">
            <JobInfo job={job} onUpdate={() => fetchJob(contractorId)} contractorId={contractorId} />
          </TabsContent>

          {/* Documents tab - hidden when accessed from workflow */}
          {!isFromWorkflow && (
            <TabsContent value="documents" className="space-y-4 mt-4">
              <JobDocuments
                ref={documentsRef}
                jobId={job.id}
                contractorId={contractorId}
                jobAddress={job.location || job.title}
                onDocumentsChange={(docs) => {
                  // Only clear compliance result if document count actually changed (add/delete)
                  const newDocIds = docs.map(d => d.id).sort().join(',');
                  const oldDocIds = jobDocuments.map(d => d.id).sort().join(',');
                  const documentsActuallyChanged = newDocIds !== oldDocIds;

                  // Transform documents for compliance checklist
                  setJobDocuments(docs.map(d => ({
                    id: d.id,
                    category: d.category || '',
                    fileName: d.document_name,
                    status: d.status as 'valid' | 'invalid' | 'needs_review' | 'uploaded' | undefined
                  })));

                  // Clear compliance result only when documents are added/removed
                  if (documentsActuallyChanged && complianceResult) {
                    setComplianceResult(null);
                  }
                }}
              />

              {/* User Form Library - Phase 7 */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Permit Forms</CardTitle>
                </CardHeader>
                <CardContent>
                  <UserFormLibrary
                    jobId={job.id}
                    compact
                    onFormFilled={(pdfBase64, formName) => {
                      toast({
                        title: "Form Filled",
                        description: `${formName} has been filled with job data and downloaded.`,
                      });
                    }}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Photos tab - hidden when accessed from workflow */}
          {!isFromWorkflow && (
            <TabsContent value="photos" className="space-y-4 mt-4">
              <JobPhotos jobId={job.id} contractorId={contractorId} />
            </TabsContent>
          )}

          {/* Inspections tab - hidden when accessed from workflow */}
          {!isFromWorkflow && (
            <TabsContent value="inspections" className="space-y-4 mt-4">
              {/* Inspection Status Card - only shown when submitted */}
              {job.inspectionStatus && (
                <InspectionStatusCard
                  status={job.inspectionStatus}
                submittedAt={job.submittedForPermitsAt}
                scheduledAt={job.inspectionScheduledAt}
                completedAt={job.inspectionCompletedAt}
                readinessScore={0} // TODO: Calculate from checklist
                blockerCount={0} // TODO: Calculate from checklist
                checklistCompletion={0} // TODO: Calculate from checklist
                onMarkVerified={async () => {
                  try {
                    await dualWriteJob(contractorId, job.id, {
                      inspectionStatus: 'verified',
                    });
                    setJob(prev => prev ? { ...prev, inspectionStatus: 'verified' } : null);
                    toast({
                      title: "Marked as Verified",
                      description: "This job is now ready for inspection scheduling.",
                    });
                  } catch (error) {
                    toast({
                      title: "Error",
                      description: "Failed to update inspection status.",
                      variant: "destructive",
                    });
                  }
                }}
              />
            )}

            {/* LLM-Generated Job Checklist */}
            {job.jurisdictionId && (
              <JobChecklist
                jobId={job.id}
                checklist={job.checklist || null}
                onChecklistUpdate={(updatedChecklist) => {
                  setJob(prev => prev ? { ...prev, checklist: updatedChecklist } : null);
                }}
              />
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  Inspector Notes & Intelligence
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {job.jurisdictionId ? (
                  <div className="space-y-4">
                    <p className="text-muted-foreground text-sm">
                      Community tips and notes for {job.jurisdictionAhjName || 'this jurisdiction'}.
                    </p>

                    {notesLoading ? (
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <Skeleton key={i} className="h-24 w-full" />
                        ))}
                      </div>
                    ) : jurisdictionNotes.length === 0 ? (
                      <div className="bg-muted/50 rounded-lg p-4 text-center">
                        <ClipboardList className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          No notes yet for this jurisdiction. Be the first to contribute!
                        </p>
                        <Button
                          variant="outline"
                          className="mt-3"
                          onClick={() => navigate('/community')}
                        >
                          Browse Community Notes
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {jurisdictionNotes.slice(0, 5).map((note) => (
                          <div
                            key={note.id}
                            className="border rounded-lg p-3 hover:shadow-sm transition-shadow"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-xs capitalize">
                                {getNoteTypeLabel(note.note_type)}
                              </Badge>
                              <Badge
                                variant={note.evidence_level === 'documented' ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {note.evidence_level}
                              </Badge>
                            </div>
                            <p className="text-sm">{note.content}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className={`h-7 px-2 ${note._userVote === 1 ? 'text-green-600' : ''}`}
                                onClick={() => handleVoteOnNote(note.id, note._userVote || null, 1)}
                              >
                                <ThumbsUp className="h-3 w-3 mr-1" />
                                {note.upvote_count}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={`h-7 px-2 ${note._userVote === -1 ? 'text-red-600' : ''}`}
                                onClick={() => handleVoteOnNote(note.id, note._userVote || null, -1)}
                              >
                                <ThumbsDown className="h-3 w-3 mr-1" />
                                {note.downvote_count}
                              </Button>
                            </div>
                          </div>
                        ))}
                        {jurisdictionNotes.length > 5 && (
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => navigate('/community')}
                          >
                            View All {jurisdictionNotes.length} Notes
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-50 dark:bg-yellow-950 rounded-lg p-4 text-center border border-yellow-200 dark:border-yellow-900">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      Add a jurisdiction to this job to see relevant inspector notes and tips.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-3"
                      onClick={() => setShowJurisdictionDialog(true)}
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Add Jurisdiction
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Checklists Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckSquare className="h-5 w-5" />
                  Pre-Inspection Checklists
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Active Checklist Runs */}
                {runsLoading ? (
                  <Skeleton className="h-20 w-full" />
                ) : checklistRuns.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Active Checklists</p>
                    {checklistRuns.map((run) => (
                      <div
                        key={run.id}
                        className="border rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer"
                        onClick={() => {
                          toast({
                            title: "Checklist View",
                            description: "Detailed checklist view coming soon.",
                          });
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">
                              {run._template?.title || 'Checklist'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Status: {run.status.replace(/_/g, ' ')}
                            </p>
                          </div>
                          <div className="text-right">
                            {run.readiness_score !== null && (
                              <Badge variant={run.readiness_score >= 80 ? 'default' : 'secondary'}>
                                {run.readiness_score}% Ready
                              </Badge>
                            )}
                            {run.blocker_count > 0 && (
                              <Badge variant="destructive" className="ml-2">
                                {run.blocker_count} Blockers
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Available Templates */}
                {checklistTemplates.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Available Checklists</p>
                    {checklistTemplates.slice(0, 3).map((template) => (
                      <div
                        key={template.id}
                        className="border rounded-lg p-3 flex items-center justify-between"
                      >
                        <div>
                          <p className="font-medium text-sm">{template.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {template._itemCount || 0} items • v{template.version}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStartChecklist(template.id)}
                          disabled={createChecklistRun.isPending}
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Start
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {checklistRuns.length === 0 && checklistTemplates.length === 0 && (
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <CheckSquare className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      No checklists available for this jurisdiction yet.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-3"
                      onClick={() => navigate('/community')}
                    >
                      Browse Community Resources
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            </TabsContent>
          )}

          <TabsContent value="compliance" className="space-y-4 mt-4">
            {/* Risk Summary Banner - Aggregate risk at a glance */}
            {complianceResult && (
              <RiskSummaryBanner
                verifiedCount={riskData.verifiedCount}
                reviewItems={riskData.reviewItems}
                blockers={riskData.blockers}
                onRunComplianceCheck={() => navigate(`/jobs/${id}/workflow`)}
                isRunningCheck={false}
                lastCheckTime={complianceResult.checkRunId ? 'Recent' : undefined}
              />
            )}

            {/* Three-Part Progress Indicator - Data, Packet, Compliance */}
            <ThreePartProgress
              data={dataProgress}
              packet={packetProgress}
              compliance={complianceProgress}
            />

            {/* Jurisdiction Card Section */}
            {job.jurisdictionId && job.jurisdictionAhjName && (
              <Card className="animate-fade-in">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5" />
                      Permitting Authority (AHJ)
                    </span>
                    <ConfidenceBadge
                      level="high"
                      source="Address geocoding"
                      size="md"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <JurisdictionCard
                    jurisdiction={{
                      id: job.jurisdictionId,
                      code: job.jurisdictionCode || "",
                      ahjName: job.jurisdictionAhjName,
                      ahjShortCode: job.jurisdictionCode || "",
                      scopeLevel: "county",
                      phone: job.jurisdictionPhone || "",
                      email: job.jurisdictionEmail || "",
                      website: job.jurisdictionWebsite || "",
                      portalUrl: job.jurisdictionPortalUrl || "",
                      overlays: {
                        floodZone: job.jurisdictionOverlays?.floodZone || false,
                        coastalZone: job.jurisdictionOverlays?.coastalZone || false,
                        historicDistrict: job.jurisdictionOverlays?.historicDistrict || false,
                        windBorneDebrisRegion: job.jurisdictionOverlays?.windBorneDebrisRegion || false,
                        highVelocityHurricaneZone: job.jurisdictionOverlays?.highVelocityHurricaneZone || false,
                      },
                      verificationStatus: "verified",
                      dataSource: "compliance_engine",
                    }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Quick Reference Card - Shows fee, timeline, contact, NOC info */}
            {job.jurisdictionId && jurisdictionDetail && (
              <QuickReferenceCard
                jurisdiction={{
                  id: jurisdictionDetail.id,
                  code: jurisdictionDetail.code,
                  ahjName: jurisdictionDetail.ahj_name,
                  ahjShortCode: jurisdictionDetail.ahj_short_code || "",
                  scopeLevel: jurisdictionDetail.scope_level,
                  phone: jurisdictionDetail.phone || "",
                  email: jurisdictionDetail.email || "",
                  website: jurisdictionDetail.website || "",
                  portalUrl: jurisdictionDetail.portal_url || "",
                  overlays: {
                    floodZone: jurisdictionDetail.is_flood_zone || false,
                    coastalZone: jurisdictionDetail.is_coastal_zone || false,
                    historicDistrict: jurisdictionDetail.is_historic_district || false,
                    windBorneDebrisRegion: jurisdictionDetail.is_wind_borne_debris_region || false,
                    highVelocityHurricaneZone: jurisdictionDetail.is_hvhz || false,
                  },
                  verificationStatus: jurisdictionDetail.verification_status || "pending",
                  dataSource: jurisdictionDetail.data_source || "",
                  // Extended JSONB fields
                  feeSchedule: jurisdictionDetail.fee_schedule,
                  processingTimelines: jurisdictionDetail.processing_timelines,
                  inspectionRequirements: jurisdictionDetail.inspection_requirements,
                  departmentContacts: jurisdictionDetail.department_contacts,
                  noticeOfCommencement: jurisdictionDetail.notice_of_commencement,
                  tradeSpecificForms: jurisdictionDetail.trade_specific_forms,
                  ownerBuilderRules: jurisdictionDetail.owner_builder_rules,
                  permitExemptions: jurisdictionDetail.permit_exemptions,
                  physicalAddress: jurisdictionDetail.physical_address,
                  officeHours: jurisdictionDetail.office_hours,
                  onlinePortal: jurisdictionDetail.online_portal,
                  // New fields from migration 043
                  permittingProcess: jurisdictionDetail.permitting_process,
                  contractorWarnings: jurisdictionDetail.contractor_warnings,
                }}
                trade={job.permitType || "general"}
                projectValue={job.estimatedValue}
              />
            )}

            {/* Trade Requirements Card - Shows trade-specific forms and checkpoints */}
            {job.jurisdictionId && jurisdictionDetail && job.permitType && (
              <TradeRequirementsCard
                jurisdiction={{
                  id: jurisdictionDetail.id,
                  code: jurisdictionDetail.code,
                  ahjName: jurisdictionDetail.ahj_name,
                  ahjShortCode: jurisdictionDetail.ahj_short_code || "",
                  scopeLevel: jurisdictionDetail.scope_level,
                  phone: jurisdictionDetail.phone || "",
                  email: jurisdictionDetail.email || "",
                  website: jurisdictionDetail.website || "",
                  portalUrl: jurisdictionDetail.portal_url || "",
                  overlays: {
                    floodZone: jurisdictionDetail.is_flood_zone || false,
                    coastalZone: jurisdictionDetail.is_coastal_zone || false,
                    historicDistrict: jurisdictionDetail.is_historic_district || false,
                    windBorneDebrisRegion: jurisdictionDetail.is_wind_borne_debris_region || false,
                    highVelocityHurricaneZone: jurisdictionDetail.is_hvhz || false,
                  },
                  verificationStatus: jurisdictionDetail.verification_status || "pending",
                  dataSource: jurisdictionDetail.data_source || "",
                  // Extended JSONB fields
                  feeSchedule: jurisdictionDetail.fee_schedule,
                  processingTimelines: jurisdictionDetail.processing_timelines,
                  inspectionRequirements: jurisdictionDetail.inspection_requirements,
                  departmentContacts: jurisdictionDetail.department_contacts,
                  noticeOfCommencement: jurisdictionDetail.notice_of_commencement,
                  tradeSpecificForms: jurisdictionDetail.trade_specific_forms,
                  ownerBuilderRules: jurisdictionDetail.owner_builder_rules,
                  permitExemptions: jurisdictionDetail.permit_exemptions,
                  physicalAddress: jurisdictionDetail.physical_address,
                  officeHours: jurisdictionDetail.office_hours,
                  onlinePortal: jurisdictionDetail.online_portal,
                  // New fields from migration 043
                  permittingProcess: jurisdictionDetail.permitting_process,
                  contractorWarnings: jurisdictionDetail.contractor_warnings,
                  // New fields from migration 044
                  commercialRequirements: jurisdictionDetail.commercial_requirements,
                }}
                trade={job.permitType}
                isOwnerBuilder={job.isOwnerBuilder}
              />
            )}

            {/* Commercial Requirements Card - Shows commercial permitting process */}
            {job.jurisdictionId && jurisdictionDetail && job.propertyType === 'commercial' && (
              <CommercialRequirementsCard
                jurisdiction={{
                  id: jurisdictionDetail.id,
                  code: jurisdictionDetail.code,
                  ahjName: jurisdictionDetail.ahj_name,
                  ahjShortCode: jurisdictionDetail.ahj_short_code || "",
                  scopeLevel: jurisdictionDetail.scope_level,
                  phone: jurisdictionDetail.phone || "",
                  email: jurisdictionDetail.email || "",
                  website: jurisdictionDetail.website || "",
                  portalUrl: jurisdictionDetail.portal_url || "",
                  overlays: {
                    floodZone: jurisdictionDetail.is_flood_zone || false,
                    coastalZone: jurisdictionDetail.is_coastal_zone || false,
                    historicDistrict: jurisdictionDetail.is_historic_district || false,
                    windBorneDebrisRegion: jurisdictionDetail.is_wind_borne_debris_region || false,
                    highVelocityHurricaneZone: jurisdictionDetail.is_hvhz || false,
                  },
                  verificationStatus: jurisdictionDetail.verification_status || "pending",
                  dataSource: jurisdictionDetail.data_source || "",
                  // Extended JSONB fields
                  departmentContacts: jurisdictionDetail.department_contacts,
                  permittingProcess: jurisdictionDetail.permitting_process,
                  commercialRequirements: jurisdictionDetail.commercial_requirements,
                }}
              />
            )}

            {/* Submission Guidance - How to submit the permit application */}
            {job.jurisdictionId && complianceResult?.jurisdiction?.submissionGuidance && (
              <SubmissionGuidance
                guidance={complianceResult.jurisdiction.submissionGuidance}
                jurisdictionName={complianceResult.jurisdiction.ahjName}
                onOpenPortal={() => {
                  toast({
                    title: "Portal Opened",
                    description: "Good luck with your permit submission!",
                  });
                }}
              />
            )}

            {/* Review Comments - Track and resolve reviewer feedback */}
            {job.jurisdictionId && (
              <ReviewComments
                comments={reviewComments}
                onAddComment={() => {
                  setEditingComment(null);
                  setShowAddCommentDialog(true);
                }}
                onImportEmail={() => setShowImportEmailDialog(true)}
                onUpdateStatus={async (commentId, status) => {
                  try {
                    await updateCommentStatus(commentId, status);
                    toast({
                      title: "Status Updated",
                      description: `Comment marked as ${status.replace('_', ' ')}`,
                    });
                  } catch (error) {
                    toast({
                      title: "Error",
                      description: "Failed to update comment status",
                      variant: "destructive",
                    });
                  }
                }}
                onEdit={(comment) => {
                  setEditingComment(comment);
                  setShowAddCommentDialog(true);
                }}
                onDelete={async (commentId) => {
                  try {
                    await deleteComment(commentId);
                    toast({
                      title: "Comment Deleted",
                      description: "The review comment has been removed",
                    });
                  } catch (error) {
                    toast({
                      title: "Error",
                      description: "Failed to delete comment",
                      variant: "destructive",
                    });
                  }
                }}
              />
            )}

            {/* Form Generation */}
            {job.jurisdictionId && job.permitType && (
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className="h-5 w-5 text-primary" />
                    <span>Permit Application Forms</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Auto-generate pre-filled permit application forms based on your job details and jurisdiction requirements.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() => setShowFormGenerationDialog(true)}
                    >
                      <Download className="h-4 w-4" />
                      Generate Permit Application
                    </Button>
                    {job.jurisdictionPortalUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(job.jurisdictionPortalUrl!, '_blank')}
                        className="gap-1 text-muted-foreground"
                      >
                        <MapPin className="h-3.5 w-3.5" />
                        Go to Jurisdiction Portal
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contextual Tips from Inspector Notes */}
            {job.jurisdictionId && (
              <ContextualTips
                jurisdictionId={job.jurisdictionId}
                trade={job.permitType === 'electrical' ? 'electrical' :
                       job.permitType === 'plumbing' ? 'plumbing' :
                       job.permitType === 'hvac' ? 'hvac' :
                       job.permitType === 'roofing' ? 'roofing' : undefined}
                limit={5}
                defaultExpanded={true}
              />
            )}

            {/* No Jurisdiction Warning */}
            {!job.jurisdictionId && (
              <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <ShieldCheck className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-800 dark:text-yellow-200">Jurisdiction Not Set</p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300">
                          This job was created before jurisdiction detection was available. Add a jurisdiction to enable compliance checking.
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setShowJurisdictionDialog(true)}
                      className="shrink-0"
                    >
                      <MapPin className="h-4 w-4 mr-1" />
                      Add Jurisdiction
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Form Recommendations - Phase 7.2 */}
            {job.jurisdictionId && job.permitType && (
              <FormRecommendations
                jurisdictionId={job.jurisdictionId}
                permitType={job.permitType}
                jobScope={job.jobScope ?? undefined}
                jobId={job.id}
                onFormFilled={(pdfBase64, formName) => {
                  toast({
                    title: "Form Filled",
                    description: `${formName} has been filled with job data and downloaded.`,
                  });
                }}
                onUploadClick={() => {
                  setActiveTab("documents");
                }}
              />
            )}

            {/* Compliance Checklist - Only show if jurisdiction is set */}
            {job.jurisdictionId && job.permitType && (
              <ComplianceChecklist
                jurisdictionId={job.jurisdictionId}
                permitType={job.permitType}
                jobScope={job.jobScope || undefined}
                jobDocuments={jobDocuments}
                onUploadClick={(category, requirement) => {
                  // Switch to documents tab using state (controlled tabs)
                  setActiveTab("documents");
                  // Use ref to trigger upload with category pre-selected after tab renders
                  setTimeout(() => {
                    documentsRef.current?.openUpload(category, requirement?.name);
                  }, 150);
                }}
                onFillClick={(formId, formName) => {
                  // Open the form fill dialog - handled by ComplianceChecklist internally
                  // This callback can be used for analytics or additional actions
                  console.log(`Opening fill for form: ${formName} (${formId})`);
                }}
              />
            )}

            {/* Compliance Check Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5" />
                  Compliance Check
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Run a compliance check to verify this job meets all Florida permit requirements for the selected jurisdiction and permit type.
                </p>

                {complianceResult && (
                  <div className="space-y-4">
                    {/* Score and Status Display */}
                    <div className="flex flex-col sm:flex-row items-center gap-6 p-4 bg-muted/50 rounded-lg">
                      <ComplianceScore
                        score={complianceResult.score}
                        status={complianceResult.status}
                        summary={complianceResult.summary}
                        size="md"
                      />
                      <div className="flex-1 text-center sm:text-left">
                        {complianceResult.jurisdiction && (
                          <div className="mb-2">
                            <p className="text-sm font-medium">
                              {complianceResult.jurisdiction.ahjName}
                            </p>
                            {complianceResult.jurisdiction.isHvhz && (
                              <Badge variant="destructive" className="mt-1">
                                HVHZ - High Velocity Hurricane Zone
                              </Badge>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Check completed in {complianceResult.runDurationMs}ms
                          {complianceResult.checkRunId && (
                            <span className="block">Run ID: {complianceResult.checkRunId}</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Findings List */}
                    {complianceResult.findings && complianceResult.findings.length > 0 && (
                      <FindingsList
                        findings={complianceResult.findings}
                        onUploadDocument={(category) => {
                          // Switch to documents tab using state (controlled tabs)
                          setActiveTab("documents");
                          // Use ref to trigger upload with category pre-selected after tab renders
                          setTimeout(() => {
                            documentsRef.current?.openUpload(category);
                          }, 150);
                        }}
                      />
                    )}

                    {/* Ready for Export - show when no CRITICAL findings (warnings are OK) */}
                    {complianceResult.findings && !complianceResult.findings.some(f => f.severity === 'critical') && (
                      <div className={`text-center p-6 rounded-lg border ${
                        complianceResult.findings.length === 0
                          ? "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-900"
                          : "bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-900"
                      }`}>
                        <CheckCircle className={`h-12 w-12 mx-auto mb-2 ${
                          complianceResult.findings.length === 0 ? "text-green-500" : "text-amber-500"
                        }`} />
                        <p className={`font-medium ${
                          complianceResult.findings.length === 0
                            ? "text-green-800 dark:text-green-200"
                            : "text-amber-800 dark:text-amber-200"
                        }`}>
                          {complianceResult.findings.length === 0
                            ? "All Compliance Requirements Met"
                            : "No Critical Issues Found"}
                        </p>
                        <p className={`text-sm mb-4 ${
                          complianceResult.findings.length === 0
                            ? "text-green-600 dark:text-green-400"
                            : "text-amber-600 dark:text-amber-400"
                        }`}>
                          {complianceResult.findings.length === 0
                            ? "This job is ready for permit submission"
                            : `${complianceResult.findings.length} warning(s) found - you can still submit for inspection`}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          {/* Download Documents Package */}
                          <Button
                            variant="outline"
                            className={complianceResult.findings.length === 0
                              ? "border-green-600 text-green-700 hover:bg-green-100 dark:border-green-500 dark:text-green-400 dark:hover:bg-green-900"
                              : "border-amber-600 text-amber-700 hover:bg-amber-100 dark:border-amber-500 dark:text-amber-400 dark:hover:bg-amber-900"
                            }
                            onClick={() => {
                              toast({
                                title: "Downloading Package",
                                description: "Your compliance document package is being prepared for download.",
                              });
                            }}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download Documents
                          </Button>

                          {/* Export Full Package */}
                          <Button
                            className={complianceResult.findings.length === 0
                              ? "bg-green-600 hover:bg-green-700"
                              : "bg-amber-600 hover:bg-amber-700"
                            }
                            onClick={() => {
                              toast({
                                title: "Export Package",
                                description: "Approval-Ready Package export will be available soon. This feature generates a ZIP file with all documents organized for permit submission.",
                              });
                            }}
                          >
                            <Package className="h-4 w-4 mr-2" />
                            Export Approval-Ready Package
                          </Button>
                        </div>

                        {/* Mark as Submitted Section */}
                        <div className="mt-6 pt-4 border-t border-border">
                          {job.inspectionStatus ? (
                            // Already submitted - show status and link
                            <div className="flex items-center justify-center gap-2 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-sm text-green-700 dark:text-green-300">
                                Submitted for inspection
                              </span>
                              <Button
                                variant="link"
                                size="sm"
                                className="text-green-600 hover:text-green-800"
                                onClick={() => setActiveTab("inspections")}
                              >
                                View Inspections Tab →
                              </Button>
                            </div>
                          ) : (
                            // Not yet submitted - show pre-submission checklist
                            <PreSubmissionChecklist
                              // Document validation
                              totalRequiredDocs={complianceResult.findings?.filter(f => f.severity === 'critical' && f.type === 'missing_document').length || 0}
                              uploadedRequiredDocs={jobDocuments.length}
                              invalidDocs={jobDocuments.filter(d => d.status === 'invalid').length}
                              missingDocs={complianceResult.findings
                                ?.filter(f => f.severity === 'critical' && f.type === 'missing_document')
                                .map(f => f.message) || []}
                              // Compliance validation
                              complianceScore={complianceResult.score}
                              complianceStatus={complianceResult.status}
                              criticalFindings={complianceResult.findings?.filter(f => f.severity === 'critical').length || 0}
                              warningFindings={complianceResult.findings?.filter(f => f.severity === 'warning').length || 0}
                              // Conditional requirements
                              nocRequired={(job.estimatedValue || 0) >= nocThresholdAmount}
                              nocAcknowledged={true} // TODO: Track this in job state
                              nocThreshold={nocThresholdAmount}
                              projectValue={job.estimatedValue || undefined}
                              hvhzRequired={complianceResult.jurisdiction?.isHvhz || false}
                              hvhzDocsUploaded={jobDocuments.some(d => d.category?.toLowerCase().includes('noa') || d.category?.toLowerCase().includes('fbc'))}
                              ownerBuilderRequired={job.isOwnerBuilder || false}
                              ownerBuilderAcknowledged={true} // TODO: Track this in job state
                              // State
                              isValidating={false}
                              // Actions - all compliance checks should go through workflow
                              onRunValidation={() => navigate(`/jobs/${id}/workflow`)}
                              onProceedToSubmit={() => setShowSubmitDialog(true)}
                              onFixIssue={(categoryId, itemId) => {
                                if (categoryId === 'documents') {
                                  setActiveTab("documents");
                                } else if (categoryId === 'compliance') {
                                  // Navigate to workflow for compliance checks
                                  navigate(`/jobs/${id}/workflow`);
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Run compliance check in workflow - button removed, use workflow instead */}
                <Button
                  onClick={() => navigate(`/jobs/${id}/workflow`)}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  {complianceResult ? 'Re-run in Workflow' : 'Run Compliance Check in Workflow'}
                </Button>
              </CardContent>
            </Card>

            {/* Audit & Decision Trail - Track all system decisions and user actions */}
            {id && (
              <ConnectedAuditTrail
                jobId={id}
                maxHeight="400px"
                showFilters={true}
                showExport={true}
              />
            )}
          </TabsContent>
        </Tabs>
      </main>

      <EditJob
        job={job}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSuccess={() => fetchJob(contractorId)}
      />

      {/* Save as Template Dialog */}
      <SaveAsTemplateDialog
        job={job}
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this job? This action cannot be undone and will also delete all associated material orders, reports, and documents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Job Status</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change the job status to <strong>{getStatusLabel(pendingStatus)}</strong>?
              <br /><br />
              This change will be immediately visible to the client in their portal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusChange} disabled={updatingStatus}>
              {updatingStatus ? "Updating..." : "Change Status"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Jurisdiction Dialog */}
      <Dialog open={showJurisdictionDialog} onOpenChange={setShowJurisdictionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Add Jurisdiction
            </DialogTitle>
            <DialogDescription>
              Select the Florida county and optionally the city where this job is located to determine the permitting authority (AHJ).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="county">County *</Label>
              <Select
                value={jurisdictionLookupCounty}
                onValueChange={setJurisdictionLookupCounty}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a county" />
                </SelectTrigger>
                <SelectContent>
                  {floridaCounties?.map((county) => (
                    <SelectItem key={county.id} value={county.name}>
                      {county.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">City (Optional)</Label>
              <Input
                id="city"
                placeholder="Enter city name"
                value={jurisdictionLookupCity}
                onChange={(e) => setJurisdictionLookupCity(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Some cities have their own building departments. Leave blank to use county jurisdiction.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowJurisdictionDialog(false);
                setJurisdictionLookupCounty("");
                setJurisdictionLookupCity("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddJurisdiction}
              disabled={savingJurisdiction || !jurisdictionLookupCounty}
            >
              {savingJurisdiction ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Looking up...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Find Jurisdiction
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Review Comment Dialog */}
      <AddReviewCommentDialog
        open={showAddCommentDialog}
        onOpenChange={setShowAddCommentDialog}
        editingComment={editingComment}
        currentReviewCycle={1}
        onSave={async (comment) => {
          if (editingComment) {
            await updateComment(editingComment.id, comment);
            toast({
              title: "Comment Updated",
              description: "The review comment has been updated",
            });
          } else {
            await addComment(comment);
            toast({
              title: "Comment Added",
              description: "The review comment has been added",
            });
          }
          setEditingComment(null);
        }}
      />

      {/* Import Email Comments Dialog */}
      <ImportEmailCommentsDialog
        open={showImportEmailDialog}
        onOpenChange={setShowImportEmailDialog}
        currentReviewCycle={1}
        onImport={async (comments) => {
          for (const comment of comments) {
            await addComment(comment);
          }
          toast({
            title: "Comments Imported",
            description: `${comments.length} comment${comments.length !== 1 ? 's' : ''} imported from email`,
          });
        }}
      />

      {/* Mark as Submitted Dialog */}
      <MarkAsSubmittedDialog
        open={showSubmitDialog}
        onOpenChange={setShowSubmitDialog}
        jobId={id || ''}
        jurisdictionName={job?.jurisdictionAhjName || 'Unknown Jurisdiction'}
        documents={jobDocuments.map(doc => ({
          id: doc.id,
          name: doc.fileName,
          type: doc.category,
          isRequired: true,
        }))}
        onSubmit={async (submissionData: SubmissionData) => {
          if (!contractorId || !id || !job) return;
          setUpdatingStatus(true);
          try {
            // Archive job documents to Evidence Vault
            await archiveJobDocumentsToVault(contractorId, id, job.location || job.title);

            // Record status change in audit trail
            await addStatusChange({
              entity: 'permit',
              previousStatus: 'draft',
              newStatus: 'submitted',
              trigger: `Submitted via ${submissionData.method.replace('_', ' ')}`,
            });

            // Update job with submission details
            await dualWriteJob(contractorId, id, {
              inspectionStatus: 'pending_verification',
              submittedForPermitsAt: submissionData.submittedAt,
              submissionConfirmation: submissionData.confirmationNumber || undefined,
              submissionMethod: submissionData.method,
            });

            // Update local state
            setJob(prev => prev ? {
              ...prev,
              inspectionStatus: 'pending_verification',
              submittedForPermitsAt: submissionData.submittedAt,
            } : null);

            toast({
              title: "Permit Marked as Submitted",
              description: submissionData.confirmationNumber
                ? `Confirmation #${submissionData.confirmationNumber} recorded. Documents archived.`
                : "Submission recorded. Documents archived to Evidence Vault.",
            });
            setActiveTab("inspections");
          } catch (error) {
            console.error('Failed to mark as submitted:', error);
            toast({
              title: "Error",
              description: "Failed to record submission. Please try again.",
              variant: "destructive",
            });
            throw error;
          } finally {
            setUpdatingStatus(false);
          }
        }}
      />

      {/* Form Generation Dialog */}
      <FormGenerationDialog
        open={showFormGenerationDialog}
        onOpenChange={setShowFormGenerationDialog}
        jobId={id || ''}
        jurisdictionCode={job?.jurisdictionCode}
        permitType={job?.permitType}
        jurisdictionId={job?.jurisdictionId || null}
        projectValue={job?.estimatedValue}
      />
    </div>
  );
};

export default JobDetail;
